<template>
  <layout-envelope
    :style="{ background: isMobileScreen ? 'white' : background }"
    class="text-gray-600"
    v-statusbar:#fff
  >
    <top-small>
      <template v-slot:left>
        <back-button class="text-interaction-dark" returnPath="election-home" />
      </template>
      <template v-slot:default>
        <navbar />
      </template>
    </top-small>
    <top-sub-b class="relative">
      <navigation-title-box
        @beforeelectionchange="onBeforeElectionChange"
        @electionchange="onElectionChanged"
      >
        <template v-slot:info>
          <span
            v-if="viewState != null && !changingElection"
            class="text-chplus-s text-color-regular whitespace-no-wrap"
            v-html="
              $t('components.nav.title_box.collection_vs_candidates', {
                collection: matchedCandidates.length,
                candidates: candidates.length
              })
            "
          ></span>
        </template>
      </navigation-title-box>
      <tutorial-button class="text-interaction-dark" :show-bus="tutorialBus" />
    </top-sub-b>
    <box-filter>
      <sorting-buttons
        :candidateIds="allCandidateIds"
        @update:candidateIds="currentCandidateIds = $event"
        @update:sortState="enableSort = $event.sortBy == null"
        :showSearch="true"
        @filterchanged="onFilterChanged"
        :initialState="viewState.filter"
        :overlay-bus="sortingButtonsBus"
        v-if="viewState != null && !changingElection"
      >
        <search-display
          class="mx-auto mb-1"
          :initial-search="
            viewState.filter != null ? viewState.filter.search : ''
          "
          :sortingBus="sortingButtonsBus"
          :lightTheme="true"
          style="min-width: 14rem; text-overflow: ellipsis;"
        />
      </sorting-buttons>
    </box-filter>
    <content-scroll-container
      class="flex-grow"
      v-if="viewState != null && !changingElection"
    >
      <content-scroll-inner class="h-full">
        <div
          ref="scrollarea"
          class="h-full overflow-x-hidden -ml-4 px-4 -mr-4 pt-2 lg:ml-0 lg:mr-0 lg:px-0"
          :class="{
            'overflow-y-auto':
              (!draggingCandidate || !isTouchInteraction) && candidatesMounted,
            'overflow-y-hidden':
              (draggingCandidate && isTouchInteraction) || !candidatesMounted
          }"
        >
          <favorites-editor
            :candidateIds="currentCandidateIds"
            :filters-active="enableSortOrFilter"
            :candidate-drag-bus="candidateDragBus"
            :style="{ opacity: changingElection ? 0.2 : null }"
            @mountedCandidateSlotGrid="onMountedCandidateSlotGrid"
          ></favorites-editor>
          <p
            class="text-chplus-xs leading-tight"
            v-html="
              $t(`views.main.favorites.instruction.${activeElection.pronoun}`, {
                council: activeElection.label
              })
            "
          ></p>
        </div>
      </content-scroll-inner>
    </content-scroll-container>
    <template v-else>
      <div class="relative" style="height:50vh;">
        <icon-loading-spinner
          :fade="true"
          color="gray"
          class="absolute-center"
        />
      </div>
    </template>
    <div
      class="relative text-right h-0 pointer-events-none"
      v-if="!hasSeenFavorites"
    >
      <FloatingButtonContainer>
        <confirm-special-button
          class="pointer-events-auto"
          @click="showConfirmation = true"
        />
      </FloatingButtonContainer>
    </div>
    <alert :show="showConfirmation">
      <template v-slot:title>
        {{ $t("views.main.favorites.progression_title") }}
      </template>
      <template v-slot:content>
        <p
          class="text-center text-chplus-regular"
          v-html="
            $t(
              `views.main.favorites.progression_text.${activeElection.pronoun}`,
              {
                council: activeElection.label
              }
            )
          "
        ></p>
      </template>
      <template v-slot:footer>
        <div class="flex w-full">
          <navigate-small-button
            :theme="'border-interaction-dark'"
            @click="showConfirmation = false"
            class="flex-1 uppercase"
            style="margin-right: 3%"
          >
            {{ $t("views.main.favorites.progression_button_1") }}
          </navigate-small-button>
          <navigate-small-button
            :theme="'contrast-light'"
            @click="unlockAchievement"
            class="flex-1"
          >
            {{ $t("views.main.favorites.progression_button_2") }}
          </navigate-small-button>
        </div>
      </template>
    </alert>
    <candidate-cursor :candidate-drag-bus="candidateDragBus"></candidate-cursor>
    <sorting-button-overlays
      :overlay-bus="sortingButtonsBus"
    ></sorting-button-overlays>
    <tutorial :show-bus="tutorialBus" />
    <achievement link="election-home" v-if="showAchievement">
      <template v-slot:subtitle>
        {{ $t("views.main.favorites.achievement_1") }}
      </template>
      <template v-slot:icon>
        <img
          class="absolute top-0 right-0 bottom-0 left-0"
          src="@/assets/achievements/unlock-pro-f03-hero.png"
        />
      </template>
      <template v-slot:info-text>
        {{ $t("views.main.favorites.achievement_2") }}
      </template>
      <template v-slot:new-achievement-text>
        {{ $t("views.main.favorites.achievement_3") }}
      </template>
    </achievement>
  </layout-envelope>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import store from "@/store";

import LayoutEnvelope from "@/components/layouts/LayoutEnvelope";
import Navbar from "@/components/nav/NavbarMain";
import Tutorial from "@/components/tutorials/TutorialFavorites";
import Alert from "@/components/layouts/Alert";
import SortingButtons from "@/components/buttons/SortingButtons";
import Achievement from "@/components/achievements/Achievement";
import ConfirmSpecialButton from "@/components/buttons/ConfirmSpecialButton";
import FavoritesEditor from "../../components/list/FavoritesEditor";
import CandidateCursor from "../../components/people/CandidateCursor";
import Vue from "vue";
import SortingButtonOverlays from "../../components/buttons/SortingButtonOverlays";
import TopSmall from "../../components/layouts/TopSmall";
import BackButton from "../../components/buttons/BackButton";
import TopSubB from "../../components/layouts/TopSubB";
import ContentScrollContainer from "../../components/layouts/ContentScrollContainer";
import ContentScrollInner from "../../components/layouts/ContentScrollInner";
import BoxFilter from "../../components/layouts/BoxFilter";
import NavigationTitleBox from "../../components/nav/NavigationTitleBox";
import TutorialButton from "@/components/buttons/TutorialButton";
import { ProgressStepId } from "@/utils/ProgressStepId";
import NavigateSmallButton from "@/components/buttons/NavigateSmallButton";
import { ViewStateMixin } from "@/utils/ViewStateMixin";
import { ProgressState } from "@/utils/ProgressState";
import constants from "@/constants";
import SearchDisplay from "@/components/buttons/SearchDisplay";
import IconLoadingSpinner from "@/components/icons/IconLoadingSpinner";
import StatusBarDirective from "@/directives/StatusBarDirective";
import FloatingButtonContainer from "../../components/buttons/FloatingButtonContainer.vue";
import IsMobileMixin from "@/utils/IsMobileMixin";

const viewId = "favorites";

export default {
  name: "Favorites",
  components: {
    IconLoadingSpinner,
    NavigateSmallButton,
    TutorialButton,
    NavigationTitleBox,
    ContentScrollInner,
    ContentScrollContainer,
    TopSubB,
    BackButton,
    TopSmall,
    SortingButtonOverlays,
    CandidateCursor,
    FavoritesEditor,
    ConfirmSpecialButton,
    LayoutEnvelope,
    Navbar,
    Tutorial,
    Alert,
    SortingButtons,
    Achievement,
    BoxFilter,
    SearchDisplay,
    FloatingButtonContainer
  },
  mixins: [ViewStateMixin, IsMobileMixin],
  directives: { statusbar: StatusBarDirective },
  async beforeRouteEnter(to, from, next) {
    if (store.getters["election/activeElection"] == null) {
      next({ name: "election-home" });
      return;
    }

    if (!store.getters["user/hasProgress"](ProgressStepId.Match)) {
      next({ name: "election-home" });
      return;
    }

    // scroll to candidate icon when returning from candidate page
    if (
      from.name === constants.CANDIDATE_ROUTE &&
      from.params &&
      from.params.candidateId
    ) {
      next(vm => {
        vm.scrollCandidate = from.params.candidateId;
      });
      return;
    }

    next();
  },
  created() {
    this.updateCandidates();
    this.candidateDragBus.$on("dragstart", this.onCandidateDragStart);
    this.candidateDragBus.$on("drag", this.onCandidateDrag);
    this.candidateDragBus.$on("dragend", this.onCandidateDragEnd);
    this.checkProgression();

    this.sortingButtonsBus.$on("search", search => (this.search = search));
  },
  async mounted() {
    await Promise.all([
      store.dispatch("election/fetchElections"),
      store.dispatch("candidate/fetchCandidates"),
      store.dispatch("candidate/fetchMatches"),
      store.dispatch("list/fetchChoice"),
      store.dispatch("list/fetchLists")
    ]);

    this.candidatesMounted = false;
    await this.fetchViewState();
  },
  data: () => ({
    viewId: viewId,
    viewState: null,
    allCandidateIds: [],
    currentCandidateIds: [],
    matches: [],
    candidatesMounted: false,
    candidateDragBus: new Vue(),
    draggingCandidate: false,
    isTouchInteraction: false,
    sortingButtonsBus: new Vue(),
    enableSort: true,
    enableSortOrFilter: false,
    showConfirmation: false,
    showAchievement: false,
    hasSeenFavorites: false,
    lastDragEvent: null,
    scrollSpeed: 0,
    baseScrollSpeed: 8,
    tutorialBus: new Vue(),
    changingElection: false,
    scrollCandidate: "",
    search: ""
  }),
  computed: {
    ...mapGetters("user", ["user", "hasProgress"]),
    ...mapGetters("election", ["activeElection"]),
    ...mapGetters("list", ["scrollTo"]),
    ...mapGetters("location", ["canton"]),
    background() {
      return this.canton(this.user.cantonId).backgroundGradient;
    },
    matchedCandidates: {
      get() {
        return this.$store.getters["candidate/matchedCandidates"];
      }
    },
    candidates: {
      get() {
        return this.$store.getters["candidate/candidates"];
      }
    }
  },
  watch: {
    // needed so that when you move someone the default sorting also gets updated.
    // otherwise when resetting the sorting it would revert to the state before the changes unless you reopen the view.
    matchedCandidates() {
      this.allCandidateIds = this.matchedCandidates;
    }
  },
  methods: {
    ...mapActions(["store", "get"]),
    ...mapActions("user", ["updateProgression"]),
    onMountedCandidateSlotGrid() {
      this.candidatesMounted = true;
      this.$nextTick(() => this.scrollToCandidate());
    },
    updateCandidates() {
      this.allCandidateIds = this.matchedCandidates;
      this.currentCandidateIds = this.matchedCandidates;
    },
    fetchViewState() {
      this.viewState = this.getViewState(this.viewId) || {};
    },
    checkProgression() {
      this.hasSeenFavorites = this.hasProgress(ProgressStepId.Favorites);
    },
    unlockAchievement() {
      if (!this.hasSeenFavorites) {
        this.showAchievement = true;
        this.hasSeenFavorites = true;
        this.updateProgression({
          key: ProgressStepId.Favorites,
          data: { state: ProgressState.Completed }
        });
      }
    },
    onCandidateDragStart(event) {
      this.draggingCandidate = true;
      this.isTouchInteraction = event.isTouchInteraction;
      this.runAutoScroll();
    },
    onCandidateDrag(event) {
      this.lastDragEvent = event;
    },
    onCandidateDragEnd() {
      this.draggingCandidate = false;
    },
    onFilterChanged(filter) {
      this.enableSortOrFilter = !!(
        filter.search ||
        filter.sorting?.sortBy ||
        filter.checkedParties?.length > 0
      );
      this.viewState.filter = filter;
      this.saveViewState();
    },
    onBeforeElectionChange() {
      this.currentCandidateIds = [];
      this.changingElection = true;
    },
    onElectionChanged() {
      this.updateCandidates();
      this.changingElection = false;
      this.fetchViewState();
    },
    runAutoScroll() {
      if (!this.draggingCandidate) {
        return;
      }
      this.updateScroll(this.lastDragEvent);
      setTimeout(() => this.runAutoScroll(), 20);
    },
    updateScroll(event) {
      if (!event) {
        return;
      }
      const scrollarea = this.$refs["scrollarea"];
      const scrollareaRect = scrollarea.getBoundingClientRect();
      const distanceToTopBorder = event.y - scrollareaRect.top;
      const distanceToBottomBorder = scrollareaRect.bottom - event.y;
      const touchingTop = distanceToTopBorder < 50;
      const touchingBottom = distanceToBottomBorder < 100;
      if (!touchingTop && !touchingBottom) {
        this.scrollSpeed = 0;
        return;
      }
      if (touchingTop && this.scrollSpeed >= 0) {
        this.scrollSpeed = -this.baseScrollSpeed;
      }
      if (touchingBottom && this.scrollSpeed <= 0) {
        this.scrollSpeed = this.baseScrollSpeed;
      }
      this.scrollSpeed *= 1.02;
      const scrollPosition = scrollarea.scrollTop;
      const scrollSpeed = this.scrollSpeed;
      const newScrollPosition = Math.min(
        scrollarea.scrollHeight,
        Math.max(0, scrollPosition + scrollSpeed)
      );
      scrollarea.scrollTo(0, newScrollPosition);
    },
    scrollToCandidate() {
      this.$nextTick(() => {
        if (!this.scrollCandidate || !this.scrollTo) {
          return;
        }

        const candidateId = this.scrollTo.candidateId;
        if (!candidateId || this.scrollCandidate !== candidateId) {
          return;
        }

        setTimeout(() => {
          const el = document.getElementById(
            `${constants.PORTRAIT_ID}${candidateId}`
          );
          if (el) {
            el.scrollIntoView({ behavior: "smooth" });
            this.$store.commit("list/scrollTo", {});
          }
        }, 500); // wait to finish setup of candidate list (uses timed events)
      });
    }
  }
};
</script>
